import React, { useState } from 'react';
import Icon from '../AppIcon';

const NotificationIndicator = ({ language = 'en' }) => {
  const [isOpen, setIsOpen] = useState(false);

  const notifications = [
    {
      id: 1,
      type: 'case',
      titleEn: 'New case assigned',
      titleFr: 'Nouveau dossier attribué',
      titleAr: 'قضية جديدة مخصصة',
      messageEn: 'Case #2024-156 has been assigned to you',
      messageFr: 'Le dossier #2024-156 vous a été attribué',
      messageAr: 'تم تعيين القضية #2024-156 لك',
      time: '5 min ago',
      icon: 'Briefcase',
      unread: true
    },
    {
      id: 2,
      type: 'calendar',
      titleEn: 'Upcoming hearing',
      titleFr: 'Audience à venir',
      titleAr: 'جلسة قادمة',
      messageEn: 'Court hearing tomorrow at 10:00 AM',
      messageFr: 'Audience au tribunal demain à 10h00',
      messageAr: 'جلسة المحكمة غدًا الساعة 10:00 صباحًا',
      time: '1 hour ago',
      icon: 'Calendar',
      unread: true
    },
    {
      id: 3,
      type: 'billing',
      titleEn: 'Payment received',
      titleFr: 'Paiement reçu',
      titleAr: 'تم استلام الدفع',
      messageEn: 'Invoice #INV-2024-089 has been paid',
      messageFr: 'La facture #INV-2024-089 a été payée',
      messageAr: 'تم دفع الفاتورة #INV-2024-089',
      time: '3 hours ago',
      icon: 'Receipt',
      unread: false
    },
    {
      id: 4,
      type: 'document',
      titleEn: 'Document ready',
      titleFr: 'Document prêt',
      titleAr: 'المستند جاهز',
      messageEn: 'Contract document has been generated',
      messageFr: 'Le document de contrat a été généré',
      messageAr: 'تم إنشاء مستند العقد',
      time: '5 hours ago',
      icon: 'FileText',
      unread: false
    },
    {
      id: 5,
      type: 'calendar',
      titleEn: 'Meeting reminder',
      titleFr: 'Rappel de réunion',
      titleAr: 'تذكير بالاجتماع',
      messageEn: 'Client meeting in 30 minutes',
      messageFr: 'Réunion client dans 30 minutes',
      messageAr: 'اجتماع العميل خلال 30 دقيقة',
      time: '25 min ago',
      icon: 'Clock',
      unread: true
    }
  ];

  const unreadCount = notifications?.filter(n => n?.unread)?.length;

  const getTitle = (notification) => {
    if (language === 'fr') return notification?.titleFr;
    if (language === 'ar') return notification?.titleAr;
    return notification?.titleEn;
  };

  const getMessage = (notification) => {
    if (language === 'fr') return notification?.messageFr;
    if (language === 'ar') return notification?.messageAr;
    return notification?.messageEn;
  };

  const headerLabel = {
    en: 'Notifications',
    fr: 'Notifications',
    ar: 'الإشعارات'
  };

  const markAllReadLabel = {
    en: 'Mark all as read',
    fr: 'Tout marquer comme lu',
    ar: 'وضع علامة مقروء على الكل'
  };

  const viewAllLabel = {
    en: 'View all notifications',
    fr: 'Voir toutes les notifications',
    ar: 'عرض جميع الإشعارات'
  };

  return (
    <div className="relative">
      <button
        className="relative p-2 rounded-lg hover:bg-muted transition-colors duration-250"
        onClick={() => setIsOpen(!isOpen)}
        aria-label="Notifications"
      >
        <Icon name="Bell" size={20} />
        {unreadCount > 0 && (
          <span className="notification-badge">
            {unreadCount}
          </span>
        )}
      </button>
      {isOpen && (
        <>
          <div
            className="fixed inset-0 z-[190]"
            onClick={() => setIsOpen(false)}
          />
          <div
            className="absolute top-full right-0 mt-2 w-96 max-w-[calc(100vw-2rem)] bg-popover border border-border rounded-lg shadow-elevation-lg z-[200] overflow-hidden"
            style={{ direction: language === 'ar' ? 'rtl' : 'ltr' }}
          >
            <div className="flex items-center justify-between px-4 py-3 border-b border-border">
              <h3 className="text-sm font-semibold text-foreground">
                {headerLabel?.[language]}
              </h3>
              <button
                className="text-xs text-primary hover:text-primary/80 transition-colors"
                onClick={() => console.log('Mark all as read')}
              >
                {markAllReadLabel?.[language]}
              </button>
            </div>

            <div className="max-h-96 overflow-y-auto">
              {notifications?.map((notification) => (
                <div
                  key={notification?.id}
                  className={`px-4 py-3 border-b border-border hover:bg-muted transition-colors cursor-pointer ${
                    notification?.unread ? 'bg-muted/50' : ''
                  }`}
                  onClick={() => console.log('Notification clicked', notification?.id)}
                >
                  <div className="flex gap-3">
                    <div className={`flex-shrink-0 w-10 h-10 rounded-lg flex items-center justify-center ${
                      notification?.type === 'case' ? 'bg-primary/10 text-primary' :
                      notification?.type === 'calendar' ? 'bg-warning/10 text-warning' :
                      notification?.type === 'billing'? 'bg-success/10 text-success' : 'bg-accent/10 text-accent'
                    }`}>
                      <Icon name={notification?.icon} size={18} />
                    </div>
                    <div className="flex-1 min-w-0">
                      <div className="flex items-start justify-between gap-2">
                        <p className="text-sm font-medium text-foreground">
                          {getTitle(notification)}
                        </p>
                        {notification?.unread && (
                          <div className="w-2 h-2 rounded-full bg-primary flex-shrink-0 mt-1" />
                        )}
                      </div>
                      <p className="text-sm text-muted-foreground mt-1">
                        {getMessage(notification)}
                      </p>
                      <p className="text-xs text-muted-foreground mt-1">
                        {notification?.time}
                      </p>
                    </div>
                  </div>
                </div>
              ))}
            </div>

            <div className="px-4 py-3 border-t border-border">
              <button
                className="w-full text-sm text-primary hover:text-primary/80 transition-colors font-medium"
                onClick={() => console.log('View all notifications')}
              >
                {viewAllLabel?.[language]}
              </button>
            </div>
          </div>
        </>
      )}
    </div>
  );
};

export default NotificationIndicator;